// Per-album PDF render + upload helper. Loads an album's session from DB,
// renders cover.pdf + interior.pdf in the browser via pdf-lib, uploads them
// to Supabase Storage at `orders/{id}/album-{n}/…` (album 1 keeps the legacy
// flat path), and tells the worker to finalize. Used by both the flow page
// (StepPreview's Confirmer) and the editor (Confirmer & soumettre) so the
// multi-album finalize step looks the same regardless of entry point.

import { renderFetch } from './renderFetch';
import {
  ordersApi,
  sessionsApi,
  photosApi,
  settingsApi,
  templatesApi,
  uploadToSignedUrl, PDF_UPLOAD,
  type AlbumSession,
  type Cover,
  type LayoutPage,
  type Photo,
} from '@/lib/api';
import { pdfPhotoSourceUrl, quickPhotoUrl } from './pdfPhotoSource';
import { previewRenderKeys, type PreviewRenderKeys } from './previewRenderKeys';
import { albumSourceHash, compositeSourceHash, partsToRender } from './albumSourceHash';
import { blankCoverZones } from './blankCoverZones';
import { backCoverPlan, isBlankBack } from './backCoverPlan';
import { readCoverElements } from './coverElements';
import { DEFAULT_PRINT_SETTINGS, type PrintSettings } from './assemblyTemplate';
import {
  generateAlbumPdfs,
  generateRedoPdfs,
  type AlbumPdfInput,
  type PageElement,
  type PdfGenProgress,
  type RedoScope,
} from '@/lib/pdfGenerator';
import { supabase } from '@/lib/supabase';
import type { CouponPrintData } from './couponPdf';
import type { TextZoneOverride, ExtraTextZone } from '@/components/CoverThumbnail';
import { emptyPagesProblem } from './emptyPages';

export interface RenderAlbumPdfsOpts {
  shopifyOrderId: string;
  albumIndex: number;
  covers: Cover[];
  /** Storiz settings (logo URL etc.) */
  logoUrl: string | null;
  /** Optional branding pages that bookend the interior PDF. */
  introPageUrl?: string | null;
  outroPageUrl?: string | null;
  /** Print bleed ("fond perdu") in mm from admin settings. Defaults to 3. */
  bleedMm?: number;
  /** ADMIN-ONLY: print the cover assembly guides (corner cut diagonals + spine
   *  ticks) on the cover wrap. Default OFF → customer PDFs are always clean. */
  showAssemblyGuides?: boolean;
  /** Progress reporter — same shape both surfaces already use. */
  onProgress?: (p: PdfGenProgress | null) => void;
  /** Force a full re-render even when the album's source hash is unchanged.
   *  Default false → unchanged albums are skipped (only edited ones re-render). */
  force?: boolean;
  /** Pre-fetched session row, to avoid re-fetching when the caller already
   *  loaded it (e.g. the skip-check in renderAndUploadAlbumPdfs). */
  prefetchedSession?: AlbumSession | null;
  /** Which photo file the PDF draws. 'print' (default) = the full-resolution
   *  original, ALWAYS for anything printed. 'preview' = the ~1600 px preview
   *  copy, for the quick 3D preview only (lib/quickPreview.ts) — never uploaded
   *  as a PDF. Same drawing code either way; only sharpness differs. */
  photoTier?: 'print' | 'preview';
  /** COUPONS (plan 2026-10-28) — PRINT RENDER ONLY (pages/internal/
   *  ServerRenderProbe via couponPrint.planOrderCoupons). The order's coupons,
   *  for the ONE album whose free leaf carries them. Absent → nothing changes. */
  coupons?: CouponPrintData | null;
  /** The coupon hash input for this album (couponPrint.couponHashInput) —
   *  joins the INTERIOR hash only, only when `coupons` is set. */
  couponHash?: unknown;
  /** The coupon pages this render drew ([] = none fitted), or null when the
   *  interior was not rebuilt (already current). Called only with `coupons`. */
  onCouponPages?: (pages: number[] | null) => void;
}

/** The hash inputs of a loaded `album_sessions` row. Declared once so the whole
 *  hash and the two per-part hashes can never read different fields. */
/**
 * B5 — the gap as hash input, ONLY when one is on. An album with no gap hashes
 * exactly as it did before the feature existed, so nothing already rendered is
 * told it changed.
 */
export function photoGapHashInput(sess: Pick<AlbumSession, 'photo_gap_mm' | 'photo_gap_color'> | null | undefined): { mm: number; color: string } | null {
  const mm = Number(sess?.photo_gap_mm ?? 0);
  if (!Number.isFinite(mm) || mm <= 0) return null;
  return { mm, color: String(sess?.photo_gap_color ?? '#FFFFFF').toUpperCase() };
}

export function hashInputFor(sess: AlbumSession | null | undefined, coupons?: unknown) {
  return {
    pages: sess?.pages ?? null,
    cover_id: sess?.cover_id ?? null,
    cover_destination_text: sess?.cover_destination_text ?? null,
    cover_year_text: sess?.cover_year_text ?? null,
    cover_customizations: sess?.cover_customizations ?? null,
    // B5 — a changed gap must re-render, or the hash-skip keeps the old book.
    photo_gap: photoGapHashInput(sess),
    // Base rules (migration 234) — ONLY when stamped, so every album without
    // the stamp hashes exactly as before and nothing re-renders by itself.
    ...(sess?.layout_rules ? { layout_rules: sess.layout_rules } : {}),
    // COUPONS (2026-10-28) — ONLY for the album whose leaf prints them
    // (couponPrint.couponHashInput). Absent → the hash of before the feature.
    ...(coupons ? { coupons } : {}),
  };
}

/** Build the albumSourceHash input from a loaded album_sessions row. Kept here
 *  so the editor (live state) and this module (DB state) hash the same fields. */
export function sessionSourceHash(sess: AlbumSession | null | undefined): string {
  return albumSourceHash(hashInputFor(sess));
}

/** What gets STORED — the two per-part hashes, so the next render can rebuild
 *  only the half that changed. See albumSourceHash.ts. */
export function sessionCompositeHash(sess: AlbumSession | null | undefined, coupons?: unknown): string {
  return compositeSourceHash(hashInputFor(sess, coupons));
}

/**
 * Load an album's session, photos, cover + customizations and assemble the
 * full `AlbumPdfInput` the pdf-lib generators consume. Shared by the normal
 * full-album render path AND the redo (réimpression) render path so neither
 * duplicates the (substantial) session-hydration logic. Returns null when the
 * album has no session or no pages.
 */
export async function buildAlbumPdfInput(opts: RenderAlbumPdfsOpts): Promise<AlbumPdfInput | null> {
  const {
    shopifyOrderId, albumIndex, covers, logoUrl,
    introPageUrl = null, outroPageUrl = null, bleedMm = 3,
  } = opts;

  // Load session for this album (cover_id, customizations, pages). Reuse the
  // caller's pre-fetched row when supplied (avoids a duplicate GET).
  const sess: AlbumSession | null = opts.prefetchedSession
    ?? (await sessionsApi.get(shopifyOrderId, albumIndex)).session;
  if (!sess?.id) {
    console.warn(`[multiAlbumPdf] album ${albumIndex} has no session; skipping`);
    return null;
  }
  {
    // ⚡ THE FOUR READS GO OUT TOGETHER (2026-10-21). They were awaited one
    // after the other — order, settings, photos, cover signature: ~1.2 s of
    // round trips measured before every render, quick 3D or print. Each is
    // still awaited where it was used, with the same fallbacks; only the
    // waiting overlaps. Nothing about what is drawn changes.
    const orderP = ordersApi.get(shopifyOrderId);
    const settingsP = settingsApi.get();
    const photosP = photosApi.list(sess.id);
    const covEarly = sess.cover_id ? (covers.find((c) => c.id === sess.cover_id) ?? null) : null;
    const coverSignP = covEarly?.storage_path
      ? supabase.storage.from('covers').createSignedUrl(covEarly.storage_path, 3600)
      : null;
    // Unobserved rejections are handled where each is awaited below.
    for (const p of [orderP, settingsP, photosP, coverSignP]) (p as Promise<unknown> | null)?.catch(() => undefined);

    // Marry-label for the bindery ("#1074 1/3") — drawn in the cover's bottom
    // turn-in margin + the interior's first page. Best-effort: a failed order
    // fetch just omits the label, never blocks PDF generation.
    let orderLabel: string | undefined;
    try {
      const { order } = await orderP;
      const BUNDLE_QTY: Record<string, number> = { Solo: 1, Duo: 2, Trio: 3 };
      const total = BUNDLE_QTY[order?.bundle ?? 'Solo'] ?? 1;
      if (order?.shopify_order_name) orderLabel = `${order.shopify_order_name} ${albumIndex}/${total}`;
    } catch (e) {
      console.warn(`[multiAlbumPdf] order fetch for marry-label (album ${albumIndex}) failed:`, e);
    }

    // Spine geometry from the global print settings (Admin → Paramètres
    // d'impression). Printed COLUMN = spine board + 2× hinge. Read once here so
    // every upload render path (editor regen, send-to-printer, redo) honours the
    // admin's spine width without per-caller plumbing. Falls back to the cover's
    // module default (16 mm column / 10 mm board) when settings are missing.
    let spineColumnMm: number | undefined;
    let spineBoardMm: number | undefined;
    let printSettings: PrintSettings | undefined;
    try {
      const { settings } = await settingsP;
      const pts = settings?.print_template_settings;
      if (pts && typeof pts.spineWmm === 'number' && typeof pts.jointGapMm === 'number') {
        spineBoardMm = pts.spineWmm;
        spineColumnMm = pts.spineWmm + 2 * pts.jointGapMm;
        // FULL geometry → the cover (drawCoverGuides) and the fiche de montage
        // (computeTemplateLayout) now derive board/sheet/spine/turn-in from this
        // ONE object, so they can never drift. Missing fields fall back to
        // DEFAULT_PRINT_SETTINGS (the historical 483×329 / 216×303 cover).
        printSettings = { ...DEFAULT_PRINT_SETTINGS, ...pts };
      }
    } catch (e) {
      console.warn(`[multiAlbumPdf] print settings fetch failed (album ${albumIndex}); using cover defaults:`, e);
    }

    const pagesPayload = (sess.pages as { pages?: LayoutPage[]; intro_elements?: PageElement[] } | null) ?? null;
    const sessPages = pagesPayload?.pages ?? null;
    // Client-placed overlay elements for the intro branding page. Stored as a
    // sibling field in the pages JSONB (migration-free). Absent on legacy /
    // unedited sessions → the intro renders as the bare branding image.
    const introElements = pagesPayload?.intro_elements ?? [];
    if (!sessPages?.length) {
      console.warn(`[multiAlbumPdf] album ${albumIndex} has no pages; skipping`);
      return null;
    }
    // A book that lost its pages never prints white (lib/emptyPages.ts, #WA-408838).
    const emptyProblem = emptyPagesProblem(sessPages);
    if (emptyProblem) throw new Error(`album ${albumIndex}: ${emptyProblem}`);

    // Model templates — REQUIRED whenever any page carries a template_id
    // (the AI assigns them by default now). Without this map the PDF's
    // template branch silently falls back to the plain grid, so a templated
    // page would print undecorated on every non-editor render path (flow
    // confirm, server background render, admin regen, redos). Fetched only
    // when actually used; failure degrades to the grid (never blocks).
    let templatesById: Map<string, import('@/lib/api').LayoutTemplate> | undefined;
    if (sessPages.some((p) => p.template_id)) {
      try {
        const { templates } = await templatesApi.list();
        templatesById = new Map((templates ?? []).map((t) => [t.id, t]));
      } catch (e) {
        console.warn(`[multiAlbumPdf] template fetch for album ${albumIndex} failed (grid fallback):`, e);
      }
    }

    // Load photos linked to that session. The list endpoint extends the
    // base Photo with `signed_url` at runtime (1h TTL) — we type the array
    // locally to pick that up without polluting the Photo interface.
    type PhotoWithUrl = Photo & { signed_url: string | null };
    let albumPhotos: PhotoWithUrl[] = [];
    try {
      const r = await photosP;
      albumPhotos = (r.photos ?? []) as PhotoWithUrl[];
    } catch (e) {
      console.warn(`[multiAlbumPdf] photo list for album ${albumIndex} failed:`, e);
    }

    // Cover lookup + signed url (cover assets live in the `covers` bucket).
    const cov = sess.cover_id ? (covers.find((c) => c.id === sess.cover_id) ?? null) : null;
    let coverImageUrl: string | null = null;
    if (cov?.storage_path && coverSignP) {
      try {
        const { data } = await coverSignP;
        coverImageUrl = data?.signedUrl ?? null;
      } catch (e) {
        console.warn(`[multiAlbumPdf] cover sign for album ${albumIndex} failed:`, e);
      }
    }

    // Cover customizations the editor wrote.
    const cust = (sess.cover_customizations as Record<string, unknown> | null) ?? {};
    const spineConfig       = (cust.spineConfig as null) ?? null;
    const backCoverBgColor  = (cust.backCoverBgColor as string | null | undefined) ?? null;
    const frontCoverBgColor = (cust.frontCoverBgColor as string | null | undefined) ?? null;
    const hideFrontCoverImage = (cust.hideFrontCoverImage as boolean | undefined) ?? false;
    const coverDesignTransform = (cust.coverDesignTransform as { x: number; y: number; scale: number } | undefined) ?? null;
    // Cover-icon override. Two flavours: { url } = customer-picked catalogue
    // icon (public covers bucket — used as-is, no signing); { storage_path } =
    // admin upload in the album-photos back-cover/ prefix (worker-signed, RLS).
    const overrideRaw =
      (cust.frontCoverImageOverride as { storage_path?: string; url?: string } | null | undefined) ?? null;
    let frontCoverImageOverrideUrl: string | null = overrideRaw?.url ?? null;
    if (!frontCoverImageOverrideUrl && overrideRaw?.storage_path) {
      try {
        const { urls } = await photosApi.signBackCoverUrls([overrideRaw.storage_path]);
        frontCoverImageOverrideUrl = urls?.[0]?.signed_url ?? null;
      } catch (e) {
        console.warn(`[multiAlbumPdf] cover-icon override sign for album ${albumIndex} failed:`, e);
      }
    }
    // Client's own front-cover image on a blank cover — stored as
    // { storage_path } in the album-photos blank-cover/ prefix; sign through
    // the worker (RLS, same as the back-cover / cover-icon prefixes).
    const blankCoverPath =
      (cust.blankCoverImage as { storage_path?: string } | null | undefined)?.storage_path ?? null;
    let blankCoverImageUrl: string | null = null;
    if (blankCoverPath) {
      try {
        const { urls } = await photosApi.signBackCoverUrls([blankCoverPath]);
        blankCoverImageUrl = urls?.[0]?.signed_url ?? null;
      } catch (e) {
        console.warn(`[multiAlbumPdf] blank-cover image sign for album ${albumIndex} failed:`, e);
      }
    }
    // Client-PDF IMPORT — full-wrap cover composite (blank-cover/ prefix, signed
    // through the worker like the other cover images). Fills the whole sheet.
    const coverWrapPath =
      (cust.coverWrapImage as { storage_path?: string } | null | undefined)?.storage_path ?? null;
    let coverFullWrapImageUrl: string | null = null;
    if (coverWrapPath) {
      try {
        const { urls } = await photosApi.signBackCoverUrls([coverWrapPath]);
        coverFullWrapImageUrl = urls?.[0]?.signed_url ?? null;
      } catch (e) {
        console.warn(`[multiAlbumPdf] cover-wrap image sign for album ${albumIndex} failed:`, e);
      }
    }
    // Client's blank-cover photo pan/zoom (mirrored in the PDF).
    const blankCoverTransform =
      (cust.blankCoverTransform as { x: number; y: number; scale: number } | null | undefined) ?? null;
    const zoneOverrides    = (cust.zoneOverrides as Record<number | string, TextZoneOverride> | undefined) ?? {};
    const extraZones       = (cust.extraZones as ExtraTextZone[] | undefined) ?? [];
    const customTexts      = (cust.customTexts as Record<number | string, string> | undefined) ?? {};
    // Back-cover photos: saved sessions persist ONLY storage_path (signed URLs
    // are short-lived, re-signed on load), so reading i.signed_url alone drops
    // every back-cover image when rendering from a re-fetched session (the
    // server render, and any flow path that re-loads). Prefer an in-memory
    // signed_url; otherwise re-sign the stored storage_path via the worker —
    // same RLS-safe path the frontCoverOverride/blankCover blocks use above.
    const backCoverImagesRaw = (cust.backCoverImages as { storage_path?: string; signed_url?: string | null; full_page?: boolean }[] | undefined) ?? [];
    // Resolve a URL per ENTRY (not a flat list) so the full_page flag stays
    // paired with its image. In-memory signed_url first; else re-sign the
    // stored storage_path (server render / reload path).
    const bcEntries = backCoverImagesRaw.map((i) => ({
      url: i.signed_url ?? null,
      path: i.storage_path ?? null,
      full: i.full_page === true,
    }));
    const toSign = bcEntries.filter((e) => !e.url && e.path);
    if (toSign.length > 0) {
      try {
        const { urls } = await photosApi.signBackCoverUrls(toSign.map((e) => e.path!));
        const byPath = new Map<string, string | null>((urls ?? []).map((u) => [u.storage_path ?? '', u.signed_url ?? null]));
        for (const e of bcEntries) {
          if (!e.url && e.path) e.url = byPath.get(e.path) ?? null;
        }
        // Order-based fallback if the API doesn't echo storage_path back.
        if (bcEntries.some((e) => !e.url && e.path) && (urls ?? []).length === toSign.length) {
          let k = 0;
          for (const e of bcEntries) {
            if (!e.url && e.path) { e.url = (urls ?? [])[k]?.signed_url ?? null; k++; }
          }
        }
      } catch (e) {
        console.warn(`[multiAlbumPdf] back-cover sign for album ${albumIndex} failed:`, e);
      }
    }
    // FULL-PAGE back photo (2026-07-15): flagged entry fills the back panel;
    // it is EXCLUDED from the grid list (parity with editor + preview). And a
    // blank cover's back photo is ALWAYS full-frame, as the editor shows it
    // (2026-10-16, #MAN-696335) — lib/backCoverPlan.ts.
    const backPlan = backCoverPlan(bcEntries, isBlankBack(cov));
    const backCoverFullPageImage = backPlan.fullPage;
    const backCoverImages = backPlan.grid;

    return {
      pages: sessPages,
      // B5 — the album's gap rides along with the pages it applies to.
      photoGapMm: Number(sess.photo_gap_mm ?? 0),
      layoutRules: sess.layout_rules ?? null,
      photoGapColor: sess.photo_gap_color ?? null,
      photos: albumPhotos.map((p) => ({
        id: p.id,
        storage_path: p.storage_path,
        // P0: full-res original for print, not the editor thumbnail tier.
        // The quick 3D preview alone asks for the preview copy (quickPhotoUrl).
        signed_url: opts.photoTier === 'preview' ? quickPhotoUrl(p) : pdfPhotoSourceUrl(p),
        // Fallback when the print original can't be fetched/decoded (e.g. a
        // HEIC libheif rejects mid-render): the worker's browser-renderable
        // JPEG preview, then the ~512px thumbnail EXPLICITLY (2026-07-13 —
        // for missing-original degraded prints the thumbnail is the only live
        // copy; signed_url alone can be a dead display-transform of the
        // missing original). Prevents the #1076 "blank grids in the PDF" bug.
        fallback_url: (p as { preview_signed_url?: string | null }).preview_signed_url
          ?? (p as { thumbnail_signed_url?: string | null }).thumbnail_signed_url
          ?? p.signed_url ?? null,
        width: p.width,
        height: p.height,
        face_data: p.face_data,
        face_count: (p as { face_count?: number | null }).face_count ?? null,
        face_spread: (p as { face_spread?: number | null }).face_spread ?? null,
        aspect_ratio: (p as { aspect_ratio?: 'landscape' | 'portrait' | 'square' | null }).aspect_ratio ?? null,
      })),
      // ⚠️ A BLANK COVER STILL HAS TEXT. `cover_id: null` IS the vierge cover —
      // it removes the artwork, and it used to remove the title with it, because
      // the PDF draws text from `cover.text_zones` and there was no cover row to
      // read them from. A customer who put his own photo on the front lost the
      // words he had just asked us to change. The picker's own '__blank__' zones
      // now live in blankCoverZones.ts and are supplied here, so the printed
      // book matches what the picker promised. See that file.
      cover: cov ? {
        id: cov.id,
        storage_path: cov.storage_path,
        thumbnail_url: cov.thumbnail_url,
        design_url: cov.design_url,
        dominant_color: cov.dominant_color,
        width_px: cov.width_px,
        text_zones: cov.text_zones,
      } : {
        id: '__blank__',
        storage_path: null,
        thumbnail_url: null,
        design_url: null,
        dominant_color: frontCoverBgColor ?? backCoverBgColor ?? '#FFFFFF',
        width_px: null,
        text_zones: blankCoverZones(Boolean(blankCoverImageUrl)),
      },
      coverImageUrl,
      destinationText: sess.cover_destination_text ?? '',
      yearText:        sess.cover_year_text ?? '',
      customTexts,
      zoneOverrides,
      extraZones,
      spineConfig,
      backCoverBgColor,
      frontCoverBgColor,
      hideFrontCoverImage,
      coverDesignTransform,
      frontCoverImageOverrideUrl,
      blankCoverImageUrl,
      blankCoverTransform,
      coverFullWrapImageUrl,
      backCoverImages,
      backCoverFullPageImage,
      // COVER ELEMENTS (2026-10-20) — stickers / icons / photos on the cover
      // faces. Photo elements resolve through `photos` above (print originals).
      coverElements: readCoverElements(cust.coverElements),
      logoUrl,
      introPageUrl,
      // `pages.hide_outro` (2026-10-30, #WA-408838): a client's own finished
      // PDF can end the book itself — no Storiz closing page, and so no outro
      // copies as ÷4 filler either (blank pages fill instead).
      outroPageUrl: (pagesPayload as { hide_outro?: boolean } | null)?.hide_outro ? null : outroPageUrl,
      introElements,
      sessionId: sess.id,
      templatesById,
      bleedMm,
      orderLabel,
      // Admin-only assembly guides on the cover wrap. Default OFF so customer
      // PDFs stay clean; an admin re-render can opt in via opts.
      showAssemblyGuides: opts.showAssemblyGuides === true,
      spineColumnMm,
      spineBoardMm,
      printSettings,
    };
  }
}

export async function renderAndUploadAlbumPdfs(opts: RenderAlbumPdfsOpts): Promise<boolean> {
  const { shopifyOrderId, albumIndex, onProgress, force } = opts;
  const setProgress = onProgress ?? (() => {});
  try {
    setProgress({ stage: 'cover', current: 0, total: 1, message: `Album ${albumIndex}…` });

    // Edit-detection: skip albums that haven't changed since their last PDF
    // generation. Multi-album confirm loops over EVERY album; without this, a
    // Trio re-renders all 3 even when only one was edited. Fetch the session
    // once, hash its source, and bail early (treating "already current" as a
    // success) when the hash still matches AND both PDFs already exist.
    const { session: sess } = await sessionsApi.get(shopifyOrderId, albumIndex);
    if (!sess?.id) {
      console.warn(`[multiAlbumPdf] album ${albumIndex} has no session; skipping`);
      return false;
    }
    const hasPdf = !!sess.cover_pdf_url && !!sess.interior_pdf_url;

    // ─── WHICH HALVES ACTUALLY NEED REBUILDING ─────────────────────────────
    //
    // A cover edit — a title, a colour, the customer's photo — used to
    // re-render 24 pages of full-resolution photos to reprint one word on the
    // front. The interior and the cover hash independently (albumSourceHash.ts,
    // verified: neither generator reads the other's inputs), so only the half
    // that changed is rebuilt. Every doubt still renders: a missing PDF, an old
    // single-hash value or `force` all come back as "do both".
    // COUPONS: this album's coupon leaf is part of its interior source — only
    // when it carries one (no `coupons` → the hash of before the feature).
    const couponHash = opts.coupons ? (opts.couponHash ?? null) : null;
    const parts = partsToRender({
      stored: sess.pdf_source_hash,
      current: hashInputFor(sess, couponHash),
      hasCoverPdf: !!sess.cover_pdf_url,
      hasInteriorPdf: !!sess.interior_pdf_url,
      force,
    });

    if (!parts.cover && !parts.interior && hasPdf) {
      console.info(`[multiAlbumPdf] album ${albumIndex} unchanged since last PDF — skipping re-render`);
      setProgress({ stage: 'done', current: 1, total: 1, message: `Album ${albumIndex} déjà à jour` });
      if (opts.coupons) { try { opts.onCouponPages?.(null); } catch { /* report only */ } }
      return true;
    }
    if (parts.cover !== parts.interior) {
      console.info(`[multiAlbumPdf] album ${albumIndex} partial re-render — cover:${parts.cover} interior:${parts.interior}`);
    }

    const input = await buildAlbumPdfInput({ ...opts, prefetchedSession: sess });
    if (!input) return false;

    // How many pages precede content page 1 in the interior just drawn — the
    // generator reports it (an intro that fails to embed shifts every page).
    const seen: { introPages: number | null; couponPages: number[] | null } = { introPages: null, couponPages: null };
    input.onInteriorPlan = (pl) => { seen.introPages = pl.introPages; };   // report only — draws nothing
    if (opts.coupons) {
      input.coupons = opts.coupons;
      input.onCouponPlan = (pl) => { seen.couponPages = pl.pages; };     // report only
    }
    const blobs = await generateAlbumPdfs(input, setProgress, parts);

    setProgress({ stage: 'done', current: 1, total: 1, message: `Téléversement album ${albumIndex}…` });
    const signed = await ordersApi.pdfUploadUrls(shopifyOrderId, albumIndex);

    // MANUAL-COVER OVERRIDE (migration 067): when an operator has replaced this
    // album's cover by hand, the worker returns cover:null and issues an
    // interior URL only. We upload just the interior — the layout and interior
    // PDF keep regenerating normally, the cover asset is left alone. There is
    // nothing to "handle" if this races: the server is the authority, and it
    // simply never hands out a URL that could overwrite the manual file.
    const coverLocked = !signed.cover;
    const interiorLocked = !signed.interior;
    if (coverLocked || interiorLocked) {
      console.info(`[multiAlbumPdf] album ${albumIndex} manual override — cover:${coverLocked} interior:${interiorLocked}; uploading only what is unlocked`);
    }
    // ⚠️ NEVER UPLOAD A HALF WE DID NOT BUILD. A skipped part comes back as an
    // empty Blob; writing that over a good PDF would replace a printable book
    // with a zero-byte file — silently, because finalize re-signs whatever is
    // in storage and would report success.
    await Promise.all([
      ...(signed.cover && parts.cover
        ? [uploadToSignedUrl(signed.cover.upload_url, new File([blobs.cover], 'cover.pdf', { type: 'application/pdf' }), 'application/pdf', undefined, PDF_UPLOAD)]
        : []),
      ...(signed.interior && parts.interior
        ? [uploadToSignedUrl(signed.interior.upload_url, new File([blobs.interior], 'interior.pdf', { type: 'application/pdf' }), 'application/pdf', undefined, PDF_UPLOAD)]
        : []),
    ]);
    // ⚠️ THE COMPOSITE, so the next render can tell the halves apart. `sourceHash`
    // above is still computed for the legacy comparison paths.
    // COUPONS: the stored hash holds the coupon leaf only if this PDF really has
    // it — a leaf that could not be drawn must be retried by the next render.
    // An interior not rebuilt this pass matched WITH the coupons: keep them.
    const couponsInPdf = !!couponHash && !interiorLocked && (!parts.interior || (seen.couponPages?.length ?? 0) > 0);
    const storedHash = couponsInPdf ? sessionCompositeHash(sess, couponHash) : sessionCompositeHash(sess);
    const finalized = await ordersApi.pdfFinalize(shopifyOrderId, albumIndex, storedHash, sess.last_saved_at ?? null);
    if (opts.coupons) {
      // A manually replaced interior is somebody's file: it holds no coupon → [].
      const drawn = interiorLocked ? [] : parts.interior ? (seen.couponPages ?? []) : null;
      try { opts.onCouponPages?.(drawn); } catch { /* report only */ }
    }

    // Flipbook preview JPEGs — rasterized from the PDFs we JUST generated, so
    // the preview the client confirms is pixel-identical to print, and every
    // regen refreshes it automatically. Lazy-imported (pdfjs stays out of the
    // editor bundle) and NON-FATAL: the submit must never fail because the
    // preview rasterizer did.
    try {
      const { generateAndUploadPreviewAssets } = await import('./previewAssets');
      // With a manual cover in force, blobs.cover is the REGENERATED cover that
      // was never uploaded — rasterizing it would leave the customer's 3D
      // flipbook showing a cover that exists nowhere else. Pull the STORED
      // cover (the operator's file, path-bound to the freshly signed URL
      // finalize just returned) and rasterize that instead, so the preview
      // matches print. If the fetch fails we skip previews rather than publish
      // a wrong cover — the worker's post-finalize server preview render
      // (which also reads the stored PDF) is the backstop.
      //
      // ⚠️ AND THE SAME APPLIES TO A HALF WE DID NOT REBUILD. A cover-only
      // render returns an EMPTY interior Blob; handing that to the rasterizer
      // would publish a flipbook with no pages at all — the customer opens
      // their preview and the book is gone. Any part not built this pass is
      // pulled from storage, exactly like the manual-cover case below.
      const storedPdf = async (url: string | undefined, what: string): Promise<Blob> => {
        if (!url) throw new Error(`${what} not rebuilt and no stored URL returned`);
        // Through the worker's R2 copy when the render cache is on (lib/renderFetch.ts); else fetch(url), as before.
        const res = await renderFetch(url);
        if (!res.ok) throw new Error(`stored ${what} fetch failed: ${res.status}`);
        return res.blob();
      };

      let coverForPreview: Blob = blobs.cover;
      if (coverLocked) {
        coverForPreview = await storedPdf(finalized?.cover_pdf_url, 'cover');
        console.info(`[multiAlbumPdf] album ${albumIndex} previews rasterized from the STORED manual cover`);
      } else if (!parts.cover) {
        coverForPreview = await storedPdf(finalized?.cover_pdf_url, 'cover');
      }

      let interiorForPreview: Blob = blobs.interior;
      if (!parts.interior) {
        interiorForPreview = await storedPdf(finalized?.interior_pdf_url, 'interior');
        console.info(`[multiAlbumPdf] album ${albumIndex} previews reuse the STORED interior (unchanged)`);
      }

      // Per-page fingerprints so the next quick preview redraws only what
      // changed. Only when THIS pass drew the interior (the intro count is then
      // observed, not assumed) and the files are the generated ones — a manual
      // cover/interior is somebody's file, not this drawing.
      let renderKeys: PreviewRenderKeys | null = null;
      if (seen.introPages != null && parts.interior && !coverLocked && !interiorLocked) {
        try { renderKeys = previewRenderKeys(input, seen.introPages); } catch { renderKeys = null; }
      }
      await generateAndUploadPreviewAssets({
        shopifyOrderId,
        albumIndex,
        coverPdf: coverForPreview,
        interiorPdf: interiorForPreview,
        printSettings: input.printSettings,
        renderKeys,
        onProgress: (p) => setProgress({
          stage: 'done', current: p.current, total: p.total,
          message: `Album ${albumIndex} — ${p.message}`,
        }),
      });
    } catch (e) {
      console.warn(`[multiAlbumPdf] preview assets for album ${albumIndex} failed (non-fatal):`, e);
    }

    return true;
  } catch (err) {
    console.error(`[multiAlbumPdf] album ${albumIndex} failed:`, err);
    // The server render reports THIS, not a bare « not-ok » (2026-10-14, #1243 / #WA-510802).
    lastAlbumRenderError = `album ${albumIndex}: ${err instanceof Error ? err.message : String(err)}`.slice(0, 400);
    return false;
  }
}

/** Why the last album render failed (null = none failed since the page loaded). */
export let lastAlbumRenderError: string | null = null;

// ─── Redo (réimpression) render + upload ──────────────────────────────────────
import { redosApi } from '@/lib/api';

export interface RenderRedoPdfsOpts extends RenderAlbumPdfsOpts {
  /** order_redos.id — drives the redos/{orderId}/{redoId}/… storage path. */
  redoId: string;
  /** orders.id (uuid) — the storage path's order folder. */
  orderId: string;
  scope: RedoScope;
  /** 0-based content-page indices for a `custom-pages` redo. */
  pageIndices?: number[];
}

export interface RenderRedoPdfsResult {
  cover_pdf_url: string | null;
  interior_pdf_url: string | null;
}

/**
 * Generate ONLY the affected redo PDFs (client-side, reusing the album
 * generators via buildAlbumPdfInput + generateRedoPdfs), upload them under
 * redos/{orderId}/{redoId}/… and return their signed download URLs. Throws if
 * the album session can't be loaded.
 */
export async function renderAndUploadRedoPdfs(opts: RenderRedoPdfsOpts): Promise<RenderRedoPdfsResult> {
  const { redoId, orderId, scope, pageIndices, albumIndex, onProgress } = opts;
  const setProgress = onProgress ?? (() => {});

  setProgress({ stage: 'cover', current: 0, total: 1, message: `Redo album ${albumIndex}…` });
  const input = await buildAlbumPdfInput(opts);
  if (!input) throw new Error(`Album ${albumIndex} introuvable pour le redo.`);

  const blobs = await generateRedoPdfs(input, scope, pageIndices, setProgress);

  let coverUrl: string | null = null;
  let interiorUrl: string | null = null;

  if (blobs.cover) {
    const signed = await redosApi.pdfUploadUrl(orderId, redoId, 'cover');
    await uploadToSignedUrl(signed.upload_url, new File([blobs.cover], 'cover.pdf', { type: 'application/pdf' }), 'application/pdf', undefined, PDF_UPLOAD);
    const fin = await redosApi.pdfFinalize(signed.storage_path);
    coverUrl = fin.download_url;
  }
  if (blobs.interior) {
    const signed = await redosApi.pdfUploadUrl(orderId, redoId, 'interior');
    await uploadToSignedUrl(signed.upload_url, new File([blobs.interior], 'interior.pdf', { type: 'application/pdf' }), 'application/pdf', undefined, PDF_UPLOAD);
    const fin = await redosApi.pdfFinalize(signed.storage_path);
    interiorUrl = fin.download_url;
  }

  return { cover_pdf_url: coverUrl, interior_pdf_url: interiorUrl };
}
